// ============================================================================
// BUILD PATIENT REQUEST (NO PHI)
//
// Pure function. Generates a plain-language patient-facing notification that
// their referral is pending additional information.
//
// ZERO PHI: no patient name / DOB / MRN / contact. Only specialty and item
// type labels — information the patient already knows about their own care.
// ============================================================================

/**
 * Build a patient-facing pending-referral notification message.
 *
 * @param {object} params
 * @param {string|null} params.receiptToken          - RG-XXXXXXX stable token
 * @param {string}      params.referralId            - Firestore referralId (fallback)
 * @param {string}      params.specialtyName         - Human-readable specialty name
 * @param {string[]}    params.missingItemTypeLabels - Human-readable missing item labels
 * @param {string|null} params.submittedAt           - ISO timestamp of submission
 * @returns {string} Plain-text message ready for clipboard
 */
export function buildPatientRequestNoPhi({
  receiptToken = null,
  referralId = '',
  specialtyName = 'Specialist',
  missingItemTypeLabels = [],
  submittedAt = null,
}) {
  const ref = receiptToken || (referralId ? referralId.slice(0, 12) : 'N/A');

  const dateStr = submittedAt
    ? new Date(submittedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'recently';

  const itemsBlock =
    missingItemTypeLabels.length > 0
      ? missingItemTypeLabels.map((t) => `  • ${t}`).join('\n')
      : '  • Please contact your provider for details';

  return [
    `RE: YOUR REFERRAL TO ${specialtyName.toUpperCase()}`,
    `Date: ${dateStr}`,
    ``,
    `Your referral to ${specialtyName} is currently pending.`,
    ``,
    `To move your referral forward, your provider's office needs:`,
    ``,
    itemsBlock,
    ``,
    `WHAT TO DO:`,
    `Contact your referring provider's office and let them know`,
    `these items are needed before your specialist referral can proceed.`,
    ``,
    `Reference: ${ref}`,
    `This message contains no personal health information.`,
  ].join('\n');
}
